import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { debugLog } from '../../utils/debug';

const MessageInput = React.memo(({ 
  newMessage, 
  onMessageChange,
  onSendMessage, 
  isLoading, 
  disabled,
  activeTab,
  cursorPosition = { start: 0, end: 0 },
  onCursorPositionChange,
  preventAutoFocus = false // 🎯 FIX: Flag to prevent auto-focus on conversation switch
}) => {
  const textareaRef = useRef(null);
  const heightAdjustmentTimeoutRef = useRef(null);
  const inputTimeoutRef = useRef(null);
  
  // Local state for immediate responsiveness
  const [localValue, setLocalValue] = useState(newMessage);
  // Track if textarea content is scrollable
  const [isScrollable, setIsScrollable] = useState(false);
  // Track wrapper height for smooth growing
  const [wrapperHeight, setWrapperHeight] = useState(44);

  // Only sync with parent when parent explicitly clears the input (after sending)
  useEffect(() => {
    if (newMessage === '') {
      setLocalValue('');
      setIsScrollable(false); // Reset scrollable state when cleared
      setWrapperHeight(44); // Reset wrapper height
      // Reset textarea inline height to ensure proper recalculation
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  }, [newMessage]);

  // Function to update wrapper height and scrollability
  const updateScrollState = useCallback(() => {
    if (textareaRef.current) {
      const scrollHeight = textareaRef.current.scrollHeight;
      
      // Calculate new wrapper height (capped at 120px)
      const newHeight = Math.min(Math.max(scrollHeight, 44), 120);
      setWrapperHeight(newHeight);
      
      // Check if scrollable
      const shouldScroll = scrollHeight > 120;
      setIsScrollable(shouldScroll);
    }
  }, []);

  // Update wrapper height and scrollability based on content
  useEffect(() => {
    updateScrollState();
  }, [localValue, updateScrollState]);

  // Listen for viewport changes (virtual keyboard open/close)
  useEffect(() => {
    const handleResize = () => {
      // Small delay to let the viewport settle after keyboard changes
      setTimeout(updateScrollState, 100);
    };

    window.addEventListener('resize', handleResize);
    // Also listen for visual viewport changes (more reliable for mobile keyboards)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, [updateScrollState]);

  // Save cursor position when losing focus (e.g., switching tabs)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !onCursorPositionChange) return;
    
    const saveCursorPosition = () => {
      onCursorPositionChange({
        start: textarea.selectionStart,
        end: textarea.selectionEnd
      });
    };
    
    textarea.addEventListener('blur', saveCursorPosition);
    return () => textarea.removeEventListener('blur', saveCursorPosition);
  }, [onCursorPositionChange]);

  // 🎯 FIX: Prevent focus during conversation switch by making textarea non-focusable
  // Only applies on mobile devices where keyboard popup is disruptive
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Only apply this fix on mobile devices (where keyboard popup is disruptive)
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
    if (!isMobile) return; // Desktop users don't have this issue
    
    if (preventAutoFocus) {
      // Track if focus was user-initiated (click/tap) vs programmatic
      let isUserInitiated = false;
      const handleMouseDown = () => { isUserInitiated = true; };
      const handleTouchStart = () => { isUserInitiated = true; };
      
      // Make textarea non-focusable and blur it immediately
      const originalTabIndex = textarea.getAttribute('tabindex');
      textarea.setAttribute('tabindex', '-1');
      
      if (document.activeElement === textarea) {
        textarea.blur();
      }
      
      // Intercept programmatic focus attempts, but allow user-initiated focus
      const preventFocus = (e) => {
        // Allow user-initiated focus (click/tap) - user wants to type
        if (isUserInitiated) {
          isUserInitiated = false; // Reset for next time
          return; // Allow the focus
        }
        
        // Block programmatic/automatic focus
        e.preventDefault();
        e.stopPropagation();
        textarea.blur();
      };
      
      // Listen for user interaction to distinguish from programmatic focus
      textarea.addEventListener('mousedown', handleMouseDown);
      textarea.addEventListener('touchstart', handleTouchStart, { passive: true });
      textarea.addEventListener('focus', preventFocus, { capture: true });
      
      return () => {
        // Restore original tabindex (or remove if it didn't exist)
        if (originalTabIndex !== null) {
          textarea.setAttribute('tabindex', originalTabIndex);
        } else {
          textarea.removeAttribute('tabindex');
        }
        textarea.removeEventListener('mousedown', handleMouseDown);
        textarea.removeEventListener('touchstart', handleTouchStart);
        textarea.removeEventListener('focus', preventFocus, { capture: true });
      };
    }
  }, [preventAutoFocus]);

  // Blur textarea when switching TO chat tab (prevents keyboard auto-opening on swipe/tab switch)
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    const prevTab = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;

    // If we just switched to 'chat' from another tab, blur to prevent keyboard popup
    if (activeTab === 'chat' && prevTab && prevTab !== 'chat') {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
      if (isMobile && textareaRef.current) {
        textareaRef.current.blur();
      }
      return; // Skip auto-focus on this transition
    }

    // Auto-focus when chat tab becomes active via other means (initial load, etc.)
    // 🎯 FIX: Skip auto-focus if preventAutoFocus is true (e.g., after conversation switch)
    if (activeTab === 'chat' && textareaRef.current && !isLoading && !disabled && !preventAutoFocus) {
      // Small delay to ensure the tab transition is complete
      const focusTimer = setTimeout(() => {
        if (textareaRef.current && !preventAutoFocus) {
          const textarea = textareaRef.current;
          textarea.focus();
          // Restore previous cursor position (or end if no previous position)
          const { start, end } = cursorPosition;
          // If both are 0, this might be initial mount, so put at end
          if (start === 0 && end === 0 && textarea.value.length > 0) {
            const length = textarea.value.length;
            textarea.setSelectionRange(length, length);
            if (onCursorPositionChange) {
              onCursorPositionChange({ start: length, end: length });
            }
          } else {
            textarea.setSelectionRange(start, end);
          }
        }
      }, 200);

      return () => clearTimeout(focusTimer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoading, disabled, preventAutoFocus]); // Added preventAutoFocus to prevent focus on conversation switch

  // Handle viewport changes for mobile keyboard
  useEffect(() => {
    const handleViewportChange = () => {
      // Small delay to ensure keyboard is fully shown
      setTimeout(() => {
        if (textareaRef.current && document.activeElement === textareaRef.current) {
          textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 150);
    };

    // Listen for viewport changes (mobile keyboard)
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, []);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on scrollHeight, with min and max constraints
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 44), 120); // Min 44px, Max 120px (about 5 lines)
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!localValue.trim() || isLoading || disabled) return;

    const messageToSend = localValue.trim();

    // Light haptic to confirm send
    import('../../utils/haptics').then(m => m.haptic('light')).catch(() => {});

    // Send the message first
    onSendMessage(messageToSend);
    
    // Clear local state immediately after sending
    setLocalValue('');
    
    // Reset wrapper height and textarea height immediately
    setWrapperHeight(44);
    setIsScrollable(false);
    
    // On iOS, we need to prevent the input from losing focus during form submission
    // Keep focus by preventing the default blur behavior
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // Reset textarea height to auto first, then it will be recalculated
      textarea.style.height = 'auto';
      
      if (isIOS) {
        // For iOS, we need to ensure focus is maintained without blur
        setTimeout(() => {
          if (textarea) {
            textarea.focus();
            // After sending, cursor should be at start (input is now empty)
            textarea.setSelectionRange(0, 0);
          }
        }, 10);
      } else {
        // For desktop browsers, the textarea maintains focus naturally on desktop
        // Height is already reset above
      }
    }
  }, [localValue, isLoading, disabled, onSendMessage]);

  // More aggressive debouncing for height adjustment (performance optimization)
  const debouncedAdjustHeight = useCallback(() => {
    if (heightAdjustmentTimeoutRef.current) {
      clearTimeout(heightAdjustmentTimeoutRef.current);
    }
    heightAdjustmentTimeoutRef.current = setTimeout(() => {
      adjustTextareaHeight();
    }, 50); // Increased debounce time for better performance
  }, [adjustTextareaHeight]);

  // Debounced parent update to reduce parent re-renders
  const debouncedParentUpdate = useCallback((value) => {
    if (inputTimeoutRef.current) {
      clearTimeout(inputTimeoutRef.current);
    }
    inputTimeoutRef.current = setTimeout(() => {
      onMessageChange(value);
    }, 100); // Increased debounce to reduce conflicts with fast typing
  }, [onMessageChange]);

  const handleMessageChange = useCallback((e) => {
    const value = e.target.value;
    
    // Update local state immediately for responsive typing
    setLocalValue(value);
    
    // Debounce parent updates to reduce re-renders
    debouncedParentUpdate(value);
    
    // Debounce height adjustment
    debouncedAdjustHeight();
    
    // Check if content is scrollable (exceeds max height)
    if (textareaRef.current) {
      const shouldScroll = textareaRef.current.scrollHeight > 120;
      setIsScrollable(shouldScroll);
    }
  }, [debouncedParentUpdate, debouncedAdjustHeight]);

  // Prevent scroll propagation on touch devices (simplified for textarea)
  const handleTouchMove = useCallback((e) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Check if the textarea is scrollable
    const isScrollableContent = textarea.scrollHeight > textarea.clientHeight;
    
    if (!isScrollableContent) {
      // If not scrollable, prevent parent page from scrolling
      e.stopPropagation();
      return;
    }
    
    // If scrollable, let textarea handle its own scrolling naturally
    // Only prevent parent scroll when at boundaries
    const isAtTop = textarea.scrollTop <= 0;
    const isAtBottom = textarea.scrollTop + textarea.clientHeight >= textarea.scrollHeight - 1;
    
    const touch = e.touches[0];
    const currentY = touch.clientY;
    
    if (!textarea.dataset.lastTouchY) {
      textarea.dataset.lastTouchY = currentY;
      return;
    }
    
    const deltaY = currentY - parseFloat(textarea.dataset.lastTouchY);
    textarea.dataset.lastTouchY = currentY;
    
    // Only prevent parent scroll at boundaries
    const scrollingUp = deltaY > 0;
    const scrollingDown = deltaY < 0;
    
    if ((isAtTop && scrollingUp) || (isAtBottom && scrollingDown)) {
      // At boundary, prevent parent page scroll
      e.stopPropagation();
    }
    // Otherwise, let the textarea scroll naturally
  }, []);

  const handleTouchStart = useCallback((e) => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Store the initial touch position
      textarea.dataset.lastTouchY = e.touches[0].clientY;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Clean up the stored touch position
      delete textarea.dataset.lastTouchY;
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (heightAdjustmentTimeoutRef.current) {
        clearTimeout(heightAdjustmentTimeoutRef.current);
      }
      if (inputTimeoutRef.current) {
        clearTimeout(inputTimeoutRef.current);
      }
    };
  }, []);

  // Mobile keyboard handling: resize root container via CSS variable
  useEffect(() => {
    const input = textareaRef.current;
    if (!input) return;

    // Native Capacitor path — use @capacitor/keyboard events for reliable sizing
    if (Capacitor.isNativePlatform()) {
      let cleanupListeners = null;

      const setupNativeKeyboard = async () => {
        try {
          const { Keyboard } = await import('@capacitor/keyboard');

          const showHandle = await Keyboard.addListener('keyboardWillShow', (info) => {
            const visualHeight = window.innerHeight - info.keyboardHeight;
            document.documentElement.style.setProperty('--visual-vh', visualHeight + 'px');
          });

          const hideHandle = await Keyboard.addListener('keyboardWillHide', () => {
            document.documentElement.style.removeProperty('--visual-vh');
          });

          cleanupListeners = () => {
            showHandle.remove();
            hideHandle.remove();
          };
        } catch (e) {
          debugLog('[MessageInput] Native keyboard setup failed:', e.message);
        }
      };

      setupNativeKeyboard();

      return () => {
        if (cleanupListeners) cleanupListeners();
        document.documentElement.style.removeProperty('--visual-vh');
      };
    }

    // Web/PWA path — use visualViewport API as fallback
    const handleViewportResize = () => {
      if (!window.visualViewport) return;
      const vv = window.visualViewport;
      const keyboardOffset = window.innerHeight - vv.height;
      if (keyboardOffset > 50) {
        document.documentElement.style.setProperty('--visual-vh', vv.height + 'px');
      } else {
        document.documentElement.style.removeProperty('--visual-vh');
      }
    };

    const handleFocus = () => {
      setTimeout(() => {
        input.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300);
    };

    window.addEventListener('resize', handleViewportResize);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    input.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('resize', handleViewportResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
      input.removeEventListener('focus', handleFocus);
      document.documentElement.style.removeProperty('--visual-vh');
    };
  }, []);

  // Slick themed input - matches app design
  // Now part of flex layout instead of fixed positioning
  return (
    <div 
      data-automation-id="chat-message-input-wrapper"
      data-testid="chat-message-input-wrapper"
      className="flex-shrink-0 bg-gray-800 border-t border-gray-700 flex items-end px-4 py-1.5 gap-3" 
      style={{
      backgroundColor: '#1f2937', // Dark gray to match app theme
      boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.3)'
    }}>
      {/* Wrapper div that grows smoothly */}
      <div 
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        style={{
          flex: 1,
          height: `${wrapperHeight}px`,
          transition: 'height 0.15s ease-out',
          position: 'relative',
          isolation: 'isolate',
          touchAction: 'pan-y' // Allow vertical panning (scrolling) but not horizontal
        }}
      >
        <textarea
        ref={textareaRef}
        id="chat-message-input"
        name="chat-message-input"
        data-testid="chat-message-input"
        data-automation-id="chat-message-input-textarea"
        aria-label="Message your coach"
        aria-describedby="chat-input-description"
        tabIndex={0}
        placeholder="What's on your mind?"
        title="Chat message input"
        value={localValue}
        onChange={handleMessageChange}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
          }
        }}
        onTouchStart={(e) => {
          // Stop propagation to prevent parent handlers from interfering
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          // Allow the textarea to handle its own scrolling
          // Don't call preventDefault() to allow native scrolling
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
        }}
        disabled={disabled}
        rows={1}
        style={{
          width: '100%',
          height: '100%',
          fontSize: '15px',
          padding: '10px 14px',
          border: '1px solid #4b5563',
          borderRadius: '20px',
          backgroundColor: '#374151',
          color: '#f3f4f6',
          resize: 'none',
          outline: 'none',
          fontFamily: 'inherit',
          lineHeight: '1.5',
          overflowY: isScrollable ? 'scroll' : 'auto',
          scrollbarWidth: isScrollable ? 'thin' : 'none',
          scrollbarColor: '#4b5563 #374151',
          WebkitOverflowScrolling: 'touch',
          boxSizing: 'border-box',
          touchAction: 'manipulation',
          overscrollBehavior: 'contain',
          // Hide webkit scrollbar when not scrollable
          ...(isScrollable ? {} : {
            msOverflowStyle: 'none'
          })
        }}
      />
      <label 
        htmlFor="chat-message-input"
        id="chat-input-label"
        data-automation-id="chat-input-label"
        style={{ 
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          borderWidth: 0
        }}
      >
        Chat message input - Type your message to the AI running coach here
      </label>
      <span 
        id="chat-input-description" 
        style={{ 
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          borderWidth: 0
        }}
        aria-hidden="true"
      >
        Type your message to the AI running coach. Press Enter to send, Shift+Enter for new line.
      </span>
      </div>
      
      {/* Send button — always visible, disabled when empty (no fake mic button) */}
      <button
        type="button"
        id="chat-send-button"
        data-testid="chat-send-button"
        aria-label="Send message"
        onClick={handleSubmit}
        disabled={isLoading || disabled || !localValue.trim()}
        className="flex items-center justify-center w-11 h-11 rounded-full bg-blue-500 text-white transition-colors disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed active:bg-blue-600 active:scale-95 flex-shrink-0"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
        </svg>
      </button>
    </div>
  );
});

MessageInput.displayName = 'MessageInput';

export default MessageInput; 